import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";
import { motion } from "framer-motion";
import HandshakeIcon from "@mui/icons-material/Handshake";
import BalanceIcon from "@mui/icons-material/Balance";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import { useLanguage } from "../../contexts/LanguageContext";
import SEOHead from "../../components/SEOHead";

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: "easeOut" },
  }),
};

const PILLAR_ICONS = [
  {
    icon: <BalanceIcon sx={{ fontSize: 32 }} />,
    key: "kooperation.pillar_law",
    color: "#1a1a2e",
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 32 }} />,
    key: "kooperation.pillar_tax",
    color: "#16213e",
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 32 }} />,
    key: "kooperation.pillar_consulting",
    color: "#0f3460",
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 32 }} />,
    key: "kooperation.pillar_partnership",
    color: "#533483",
  },
];

const STATS = [
  { value: "360°", labelKey: "kooperation.stat1_label" },
  { value: "1×", labelKey: "kooperation.stat2_label" },
  { value: "TOP", labelKey: "kooperation.stat3_label" },
  { value: "AWG", labelKey: "kooperation.stat4_label" },
];

const KooperationPage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <Box sx={{ top: 200, bgcolor: "#fff", overflow: "hidden" }}>
      <SEOHead
        title={t('seo.kooperation_title')}
        description={t('seo.kooperation_description')}
        canonicalPath="/kooperation"
      />
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: "url('/images/cooperation_2.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden",
          pt: { xs: 18, md: 22 },
          pb: { xs: 14, md: 18 },
        }}
      >
        {/* Dark overlay for readability */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(10,10,26,0.82) 0%, rgba(10,10,26,0.55) 100%)",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.3)",
                borderRadius: "100px",
                px: 2.5,
                py: 0.75,
                mb: 4,
              }}
            >
              <HandshakeIcon sx={{ fontSize: 16, color: "#818cf8" }} />
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "#818cf8",
                  fontWeight: 600,
                }}
              >
                {t("kooperation.our_partners")}
              </Typography>
            </Box>
          </MotionBox>

          <MotionBox
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.8rem", md: "4.5rem" },
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.1,
                mb: 3,
                maxWidth: 700,
              }}
            >
              {t("kooperation.title")}
            </Typography>
          </MotionBox>

          <MotionBox
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "1.1rem",
                maxWidth: 500,
                lineHeight: 1.8,
              }}
            >
              {t("kooperation.hero_tagline")}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ── Partner Card ──────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          custom={0}
          variants={fadeUp}
        >
          <Box
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #e8e8f0",
              boxShadow: "0 24px 80px rgba(0,0,0,0.08)",
            }}
          >
            {/* Top accent bar */}
            <Box
              sx={{
                height: 4,
                background: "linear-gradient(90deg, #1e3652, #2f527b, #06b6d4)",
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* Left — Logo panel */}
              <Box
                sx={{
                  width: { xs: "100%", md: 340 },
                  flexShrink: 0,
                  bgcolor: "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: { xs: 6, md: 8 },
                  gap: 3,
                  borderRight: { md: "1px solid #f0f0f5" },
                  borderBottom: { xs: "1px solid #f0f0f5", md: "none" },
                }}
              >
                <Box
                  component="a"
                  href="https://awg-legaltax.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-block" }}
                >
                  <Box
                    component="img"
                    src="/images/AWGLogo-2.png"
                    alt="AWG LegalTax Logo"
                    sx={{ width: { xs: 180, md: 220 }, objectFit: "contain" }}
                  />
                </Box>
                <Box
                  sx={{
                    borderTop: "1px solid #e8e8f0",
                    pt: 2.5,
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#aaa",
                      fontSize: "0.7rem",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("kooperation.official_partner")}
                  </Typography>
                </Box>
              </Box>

              {/* Right — Content panel */}
              <Box sx={{ flex: 1, p: { xs: 5, md: 7 } }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "#2f527b",
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {t("kooperation.partnership_label")}
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                    fontWeight: 800,
                    color: "#0a0a1a",
                    lineHeight: 1.2,
                    mb: 4,
                  }}
                >
                  {t("kooperation.partnership_title")}
                  <Box
                    component="a"
                    href="https://awg-legaltax.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "block",
                      background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    AWG LegalTax
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: "#444",
                    fontSize: "1rem",
                    lineHeight: 1.9,
                    mb: 5,
                  }}
                >
                  {t("kooperation.partnership_text")}
                </Typography>

                <Divider sx={{ mb: 4, borderColor: "#f0f0f5" }} />

                {/* Pillars */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(4, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  {PILLAR_ICONS.map((p, i) => (
                    <MotionBox
                      key={p.key}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 * i, duration: 0.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1.5,
                          p: 2.5,
                          borderRadius: 3,
                          border: "1px solid #f0f0f5",
                          bgcolor: "#fafafa",
                          textAlign: "center",
                          transition: "all 0.25s",
                          "&:hover": {
                            borderColor: "#6366f1",
                            bgcolor: "#f5f5ff",
                            transform: "translateY(-3px)",
                            boxShadow: "0 8px 24px rgba(99,102,241,0.12)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: p.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                          }}
                        >
                          {p.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: "#0a0a1a",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {t(p.key)}
                        </Typography>
                      </Box>
                    </MotionBox>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </MotionBox>
      </Container>

      {/* ── Synergies Strip ───────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fafafa",
          borderTop: "1px solid #f0f0f0",
          py: { xs: 10, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "center" },
              gap: { xs: 6, md: 12 },
            }}
          >
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
              variants={fadeUp}
              sx={{ flex: 1 }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#6366f1",
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                {t("kooperation.synergies_label")}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: "1.8rem", md: "2.4rem" },
                  fontWeight: 800,
                  color: "#0a0a1a",
                  lineHeight: 1.25,
                  mb: 3,
                }}
              >
                {t("kooperation.synergies_title1")}
                <br />
                {t("kooperation.synergies_title2")}
              </Typography>
              <Typography
                sx={{ color: "#666", lineHeight: 1.9, maxWidth: 480 }}
              >
                {t("kooperation.synergies_text")}
              </Typography>
            </MotionBox>

            {/* Stats */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 3,
                flex: 1,
              }}
            >
              {STATS.map((stat, i) => (
                <MotionBox
                  key={stat.labelKey}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * i, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      p: 3.5,
                      borderRadius: 3,
                      border: "1px solid #e8e8f0",
                      bgcolor: "#fff",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.8rem",
                        fontWeight: 900,
                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: "#888",
                        fontWeight: 500,
                      }}
                    >
                      {t(stat.labelKey)}
                    </Typography>
                  </Box>
                </MotionBox>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default KooperationPage;
